import type { TuitionMonthlyFee } from '@/types';
import { formatNumber } from '@/lib/datetime';

interface ParentTuitionMonthlyFeeProps {
  monthlyFees: TuitionMonthlyFee[];
}

export default function ParentTuitionMonthlyFee({
  monthlyFees,
}: ParentTuitionMonthlyFeeProps) {
  const now = new Date();
  const year = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const rows = monthlyFees
    .filter((f) => f.year === year)
    .sort((a, b) => a.month - b.month);

  if (rows.length === 0) return null;

  const billedRows = rows.filter((r) => !r.is_excluded);
  const total = billedRows.reduce((sum, r) => sum + (r.fee || 0), 0);
  const excludedCount = rows.length - billedRows.length;

  return (
    <div className="mt-5 rounded-xl border border-background-200 bg-background-100 p-4 md:p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-bold text-foreground-950">
            <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary-100 text-primary-700">
              <i className="ri-calendar-line"></i>
            </span>
            {year}년 월별 교습비
          </h3>
          <p className="mt-1 text-xs text-foreground-500">
            달마다 적용되는 교습비예요. 휴원·면제로 건너뛴 달은 &lsquo;제외&rsquo;로 표시돼요.
          </p>
        </div>
        <span className="rounded-full bg-background-200 px-3 py-1 text-[11px] font-semibold text-foreground-600">
          {billedRows.length}개월 · 합계 {formatNumber(total)}원
          {excludedCount > 0 ? ` · 제외 ${excludedCount}개월` : ''}
        </span>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((row) => {
          const isCurrent = row.month === currentMonth;
          if (row.is_excluded) {
            return (
              <div
                key={row.id}
                className="flex items-center justify-between rounded-lg border border-background-300 bg-background-200/70 px-3 py-2"
              >
                <span className="flex items-center gap-1.5 text-xs font-semibold text-foreground-500">
                  {row.month}월
                  <span className="flex items-center gap-1 rounded-full bg-background-300 px-1.5 py-0.5 text-[10px] font-bold text-foreground-600">
                    <i className="ri-forbid-line"></i>
                    제외
                  </span>
                </span>
                <span className="text-xs font-semibold text-foreground-400">건너뜀</span>
              </div>
            );
          }
          return (
            <div
              key={row.id}
              className={`flex items-center justify-between rounded-lg border px-3 py-2 ${
                isCurrent
                  ? 'border-primary-300 bg-primary-50'
                  : 'border-background-200 bg-background-50'
              }`}
            >
              <span
                className={`flex items-center gap-1.5 text-xs font-semibold ${
                  isCurrent ? 'text-primary-700' : 'text-foreground-700'
                }`}
              >
                {row.month}월
                {isCurrent && (
                  <span className="rounded-full bg-primary-500 px-1.5 py-0.5 text-[10px] font-bold text-background-50">
                    이번 달
                  </span>
                )}
              </span>
              <span className="text-sm font-bold text-foreground-950">
                {formatNumber(row.fee)}원
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}